// client/src/components/pages/LexiStar/Routes/RouteMap.jsx
//
// The Routes & lanes editor map (LS-09 §5.11, §6 map rules) on the shared
// LexiStarMap foundation. Layers, bottom to top:
//   geofences          purpose fill (LS-09 §4); permitted stops and required
//                      weighbridges outlined strongly, the rest faded
//   saved corridor     the engine-facing polygon of the saved version (dashed)
//   draft corridor     translucent band exactly corridor_m wide at every zoom
//                      (line-width in metres) — the live slider preview
//   advisory corridors active advisories' alternates, orange
//   compare version    an older version, grey dashed
//   golden-run track   the raw recorded track, thin grey
//   route line         the draft / current geometry, solid blue
//   markers            A (source) · B (destination) · numbered via points (draggable)
//
// Clicking a geofence toggles it (weighbridge purpose → required weighbridge,
// else permitted stop); clicking elsewhere in "add via point" mode adds one.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Source, Layer, Marker } from 'react-map-gl/maplibre';
import LexiStarMap from '../../../map/LexiStarMap';
import {
  corridorLineWidth,
  meanLat,
  bboxOf,
  geofenceFeatures,
  geofenceDots,
} from './routeGeometry';

const ROUTE_BLUE = '#1d4ed8';
const CORRIDOR_BLUE = '#3b82f6';
const ALT_ORANGE = '#f97316';
const COMPARE_GREY = '#64748b';
const GEOFENCE_LAYER = 'route-geofence-fill';
// Zoomed out, a 40 m weighbridge is sub-pixel: a dot per geofence keeps every
// zone visible and clickable at lane scale.
const GEOFENCE_DOT_LAYER = 'route-geofence-dot';
const DOTS_MAX_ZOOM = 13;
// The saved corridor's outline only reads once the band is wider than the line.
const SAVED_OUTLINE_MIN_ZOOM = 11;
const DEFAULT_VIEW = { longitude: 28.5, latitude: -26.2, zoom: 6 };

const lineFeature = (coords, properties = {}) =>
  Array.isArray(coords) && coords.length >= 2
    ? { type: 'Feature', properties, geometry: { type: 'LineString', coordinates: coords } }
    : null;

const fc = (features) => ({ type: 'FeatureCollection', features: features.filter(Boolean) });

function EndMarker({ label, tone }) {
  return (
    <div
      className={`w-7 h-7 rounded-full border-2 border-white shadow-lg flex items-center justify-center text-xs font-bold text-white ${tone}`}
    >
      {label}
    </div>
  );
}

export default function RouteMap({
  geofences = [],
  stopIds = [],
  weighbridgeIds = [],
  line = null, // [lon,lat][]
  corridorM = 300,
  savedCorridor = null, // GeoJSON Feature
  compareLine = null,
  track = null,
  alternates = [], // [{ coords, corridor_m, draft? }]
  from = null, // [lon, lat]
  to = null,
  via = [],
  addingVia = false,
  onGeofenceClick,
  onAddVia,
  onMoveVia,
  className = 'relative w-full h-[420px] lg:h-[560px] rounded-xl overflow-hidden border border-slate-200',
}) {
  const glRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);

  const geofenceFC = useMemo(
    () => geofenceFeatures(geofences, { stopIds, weighbridgeIds }),
    [geofences, stopIds, weighbridgeIds]
  );
  const dotFC = useMemo(() => geofenceDots(geofenceFC), [geofenceFC]);
  const routeFC = useMemo(() => fc([lineFeature(line)]), [line]);
  const compareFC = useMemo(() => fc([lineFeature(compareLine)]), [compareLine]);
  const trackFC = useMemo(() => fc([lineFeature(track)]), [track]);
  const altFC = useMemo(
    () =>
      fc(
        alternates.map((a, i) =>
          lineFeature(a.coords, { i, draft: !!a.draft, corridor_m: a.corridor_m || corridorM })
        )
      ),
    [alternates, corridorM]
  );
  const refLat = useMemo(
    () => meanLat(line || (from && to ? [from, to] : [])) || DEFAULT_VIEW.latitude,
    [line, from, to]
  );
  const corridorWidth = useMemo(() => corridorLineWidth(corridorM, refLat), [corridorM, refLat]);
  // Alternates carry their own width; one layer per width is overkill — use the widest.
  const altWidth = useMemo(() => {
    const w = alternates.reduce((m, a) => Math.max(m, a.corridor_m || corridorM), 0) || corridorM;
    return corridorLineWidth(w, refLat);
  }, [alternates, corridorM, refLat]);

  // Frame the route (or the two sites) whenever what is being edited changes.
  const frameKey = useMemo(() => {
    const pts = line && line.length >= 2 ? [line[0], line[line.length - 1]] : [from, to];
    return JSON.stringify(pts);
  }, [line, from, to]);
  useEffect(() => {
    if (!mapReady || !glRef.current) return;
    const pts = line && line.length >= 2 ? line : [from, to, ...via].filter(Boolean);
    const bounds = bboxOf(pts);
    if (bounds) glRef.current.fitBounds(bounds, { padding: 56, duration: 400, maxZoom: 15 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, frameKey]);

  const handleLoad = useCallback((e) => {
    glRef.current = e.target;
    setMapReady(true);
  }, []);

  const handleClick = useCallback(
    (e) => {
      const f =
        e.features &&
        e.features.find(
          (ft) => ft.layer?.id === GEOFENCE_LAYER || ft.layer?.id === GEOFENCE_DOT_LAYER
        );
      if (f && onGeofenceClick && !addingVia) {
        onGeofenceClick(Number(f.properties.geofence_id), f.properties.purpose);
        return;
      }
      if (addingVia && onAddVia && e.lngLat) onAddVia([e.lngLat.lng, e.lngLat.lat]);
    },
    [addingVia, onAddVia, onGeofenceClick]
  );

  return (
    <div className={className}>
      <LexiStarMap
        className="absolute inset-0"
        initialViewState={DEFAULT_VIEW}
        maxZoom={20}
        defaultBasemap="street"
        navigationPosition="top-left"
        interactiveLayerIds={[GEOFENCE_LAYER, GEOFENCE_DOT_LAYER]}
        cursor={addingVia ? 'crosshair' : undefined}
        onLoad={handleLoad}
        onClick={handleClick}
      >
        <Source id="route-geofences" type="geojson" data={geofenceFC}>
          <Layer
            id={GEOFENCE_LAYER}
            type="fill"
            paint={{
              'fill-color': ['get', 'fill'],
              'fill-opacity': ['match', ['get', 'role'], 'none', 0.12, 0.3],
            }}
          />
          <Layer
            id="route-geofence-line"
            type="line"
            paint={{
              'line-color': [
                'match',
                ['get', 'role'],
                'weighbridge',
                '#0f766e',
                'stop',
                '#0e7490',
                ['get', 'fill'],
              ],
              'line-width': ['match', ['get', 'role'], 'none', 1, 3],
              'line-opacity': ['match', ['get', 'role'], 'none', 0.6, 1],
            }}
          />
        </Source>

        {savedCorridor && (
          <Source id="route-saved-corridor" type="geojson" data={savedCorridor}>
            <Layer
              id="route-saved-corridor-line"
              type="line"
              minzoom={SAVED_OUTLINE_MIN_ZOOM}
              paint={{
                'line-color': ROUTE_BLUE,
                'line-width': 1.25,
                'line-opacity': 0.7,
                'line-dasharray': [3, 2],
              }}
            />
          </Source>
        )}

        <Source id="route-draft-corridor" type="geojson" data={routeFC}>
          <Layer
            id="route-draft-corridor-band"
            type="line"
            layout={{ 'line-join': 'round', 'line-cap': 'round' }}
            paint={{
              'line-color': CORRIDOR_BLUE,
              'line-opacity': 0.22,
              'line-width': corridorWidth,
            }}
          />
        </Source>

        <Source id="route-alternates" type="geojson" data={altFC}>
          <Layer
            id="route-alternates-band"
            type="line"
            layout={{ 'line-join': 'round', 'line-cap': 'round' }}
            paint={{ 'line-color': ALT_ORANGE, 'line-opacity': 0.2, 'line-width': altWidth }}
          />
          <Layer
            id="route-alternates-line"
            type="line"
            paint={{ 'line-color': ALT_ORANGE, 'line-width': 3, 'line-dasharray': [2, 1.5] }}
          />
        </Source>

        <Source id="route-compare" type="geojson" data={compareFC}>
          <Layer
            id="route-compare-line"
            type="line"
            paint={{ 'line-color': COMPARE_GREY, 'line-width': 3, 'line-dasharray': [1.5, 1.5] }}
          />
        </Source>

        <Source id="route-track" type="geojson" data={trackFC}>
          <Layer
            id="route-track-line"
            type="line"
            paint={{ 'line-color': '#475569', 'line-width': 1.5, 'line-opacity': 0.8 }}
          />
        </Source>

        <Source id="route-line" type="geojson" data={routeFC}>
          <Layer
            id="route-line-casing"
            type="line"
            layout={{ 'line-join': 'round', 'line-cap': 'round' }}
            paint={{ 'line-color': '#ffffff', 'line-width': 6 }}
          />
          <Layer
            id="route-line-stroke"
            type="line"
            layout={{ 'line-join': 'round', 'line-cap': 'round' }}
            paint={{ 'line-color': ROUTE_BLUE, 'line-width': 3.5 }}
          />
        </Source>

        <Source id="route-geofence-dots" type="geojson" data={dotFC}>
          <Layer
            id={GEOFENCE_DOT_LAYER}
            type="circle"
            maxzoom={DOTS_MAX_ZOOM}
            paint={{
              'circle-color': ['get', 'fill'],
              'circle-radius': ['match', ['get', 'role'], 'none', 5, 7],
              'circle-stroke-color': [
                'match',
                ['get', 'role'],
                'weighbridge',
                '#0f766e',
                'stop',
                '#0e7490',
                '#ffffff',
              ],
              'circle-stroke-width': ['match', ['get', 'role'], 'none', 1.5, 3],
            }}
          />
        </Source>

        {from && (
          <Marker longitude={from[0]} latitude={from[1]} anchor="center">
            <EndMarker label="A" tone="bg-emerald-600" />
          </Marker>
        )}
        {to && (
          <Marker longitude={to[0]} latitude={to[1]} anchor="center">
            <EndMarker label="B" tone="bg-amber-600" />
          </Marker>
        )}
        {via.map((p, i) => (
          <Marker
            key={`via-${i}`}
            longitude={p[0]}
            latitude={p[1]}
            anchor="center"
            draggable={!!onMoveVia}
            onDragEnd={(e) => onMoveVia && onMoveVia(i, [e.lngLat.lng, e.lngLat.lat])}
          >
            <div
              className="w-6 h-6 rounded-full bg-white border-2 border-blue-700 shadow text-[11px] font-bold text-blue-800 flex items-center justify-center cursor-move"
              title="Drag to adjust the route"
            >
              {i + 1}
            </div>
          </Marker>
        ))}
      </LexiStarMap>

      {addingVia && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] bg-blue-700 text-white text-xs font-medium px-3 py-1.5 rounded-full shadow-lg pointer-events-none">
          Click the map to add a via point
        </div>
      )}
    </div>
  );
}
